import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Globe } from 'lucide-react';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { api } from '@/config/api';
import {
  metaOAuthService,
  AvailableAdAccount,
  ConnectedAdAccount,
} from '@/services/meta-oauth.service';

interface Props {
  /** Label for the primary action button. */
  label?: string;
  /** Called once an ad account is finalized — parent should refresh integrations data. */
  onConnected?: (adAccount: ConnectedAdAccount) => void;
  /** Style variant — primary = gold gradient (default), secondary = outlined for "Reconnect". */
  variant?: 'primary' | 'secondary';
  /** Render the icon-less compact form for inline use. */
  compact?: boolean;
}

interface PostMessageSuccess {
  type: 'meta-oauth-success';
  oauthSessionId: string;
  accounts: AvailableAdAccount[];
}
interface PostMessageError {
  type: 'meta-oauth-error';
  reason: string;
}
type PostMessagePayload = PostMessageSuccess | PostMessageError;

const POPUP_FEATURES = 'width=600,height=720,resizable=yes,scrollbars=yes,status=yes';

export function ConnectMetaButton({
  label = 'Connect with Meta',
  onConnected,
  variant = 'primary',
  compact = false,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [chooserAccounts, setChooserAccounts] = useState<AvailableAdAccount[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const popupTimerRef = useRef<number | null>(null);

  const releasePopup = useCallback(() => {
    if (popupTimerRef.current !== null) window.clearInterval(popupTimerRef.current);
    popupTimerRef.current = null;
    popupRef.current?.close();
    popupRef.current = null;
  }, []);

  useEffect(() => releasePopup, [releasePopup]);

  const finalize = useCallback(
    async (oauthSessionId: string, adAccountId: string) => {
      try {
        const result = await metaOAuthService.finalize({ oauthSessionId, adAccountId });
        toast.success(result.adAccount.currency
          ? `Facebook ad account connected — bills in ${result.adAccount.currency}`
          : `Connected ${result.adAccount.adAccountName}`);
        setChooserOpen(false);
        setActiveSessionId(null);
        setChooserAccounts([]);
        onConnected?.(result.adAccount);
      } catch {
        // axios interceptor surfaces the error toast
      } finally {
        setBusy(false);
      }
    },
    [onConnected],
  );

  // Listen for the popup to postMessage back.
  useEffect(() => {
    const handler = (ev: MessageEvent<PostMessagePayload>) => {
      const callbackOrigin = new URL(api.defaults.baseURL || '/', window.location.origin).origin;
      if (!popupRef.current || ev.source !== popupRef.current || ev.origin !== callbackOrigin) return;
      const data = ev.data;
      if (!data || typeof data !== 'object' || !('type' in data)) return;
      if (data.type === 'meta-oauth-error') {
        releasePopup();
        setBusy(false);
        toast.error(typeof data.reason === 'string' ? data.reason : 'Meta connection failed. Please try again.');
        return;
      }
      if (data.type === 'meta-oauth-success') {
        const { oauthSessionId, accounts } = data;
        if (typeof oauthSessionId !== 'string' || !/^[A-Za-z0-9_-]{24}$/.test(oauthSessionId)
          || !Array.isArray(accounts) || accounts.some(account => !account
            || typeof account.id !== 'string' || !/^act_\d+$/.test(account.id)
            || typeof account.name !== 'string')) return;
        releasePopup();
        if (accounts.length === 0) {
          setBusy(false);
          toast.error('No ad accounts found on this Meta account.');
          return;
        }
        setActiveSessionId(oauthSessionId);
        if (accounts.length === 1) {
          finalize(oauthSessionId, accounts[0].id);
          return;
        }
        setChooserAccounts(accounts);
        setChooserOpen(true);
        setBusy(false);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [finalize, releasePopup]);

  const start = async () => {
    releasePopup();
    const popup = window.open('about:blank', 'meta-oauth', POPUP_FEATURES);
    if (!popup) {
      toast.error('Pop-up blocked — allow pop-ups for this site to connect Meta.');
      return;
    }
    popupRef.current = popup;
    setBusy(true);
    try {
      const { url } = await metaOAuthService.start();
      if (popup.closed || popupRef.current !== popup) {
        releasePopup();
        setBusy(false);
        return;
      }
      const authorizationUrl = new URL(url);
      if (authorizationUrl.origin !== 'https://www.facebook.com') {
        throw new Error('Invalid Meta authorization URL');
      }
      popup.location.href = authorizationUrl.toString();
      const expiresAt = Date.now() + 10 * 60_000;
      popupTimerRef.current = window.setInterval(() => {
        if (popup.closed || Date.now() >= expiresAt) {
          releasePopup();
          setBusy(false);
        }
      }, 500);
    } catch {
      releasePopup();
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        loading={busy}
        onClick={start}
        iconLeft={compact ? undefined : <Globe size={15} />}
      >
        {label}
      </Button>

      <Modal
        open={chooserOpen}
        onClose={() => {
          setChooserOpen(false);
          setBusy(false);
          setActiveSessionId(null);
        }}
        title="Choose an ad account"
      >
        <p className="text-sm text-text-muted mb-3">
          Your Meta account has access to {chooserAccounts.length} ad accounts. Pick the one to connect to this workspace.
        </p>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {chooserAccounts.map((acc) => (
            <button
              key={acc.id}
              type="button"
              disabled={busy}
              onClick={() => {
                if (!activeSessionId) return;
                setBusy(true);
                finalize(activeSessionId, acc.id);
              }}
              className="w-full text-left rounded-xl border border-border bg-surface px-4 py-3 transition-colors hover:border-primary hover:bg-surface-alt disabled:opacity-50"
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-semibold text-sm text-text-primary truncate">{acc.name || acc.id}</div>
                <span className="text-xs font-mono text-text-muted shrink-0">{acc.id}</span>
              </div>
              <div className="mt-1 flex items-center gap-3 text-xs text-text-muted">
                <span>{acc.currency || '—'}</span>
                <span>·</span>
                <span>{acc.timezone || 'No timezone'}</span>
                <span>·</span>
                <span>Status {acc.status}</span>
              </div>
            </button>
          ))}
        </div>
      </Modal>
    </>
  );
}
